import * as amplitude from '@amplitude/analytics-browser'
import { Experiment } from '@amplitude/experiment-js-client'

import type { User } from '../types/index.js'

export const KESTRIX_SURVEY_APP = 'kestrix_survey' as const

export type InitOptions = {
  autocapture?: boolean
}

export function initAmplitude(options?: InitOptions): void {
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY
  if (!apiKey) return
  amplitude.init(apiKey, { autocapture: options?.autocapture ?? true })
}

/**
 * Initialise the Amplitude Experiment client for Guides & Surveys.
 * Call once after {@link initAmplitude}, typically in the app root.
 *
 * Uses the integrated initializer so the Experiment client shares
 * user/device identity with analytics and refetches variants on
 * identity changes (e.g. after `identifyUser()`).
 */
export async function initExperiment(): Promise<void> {
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY
  const deploymentKey = import.meta.env.VITE_AMPLITUDE_DEPLOYMENT_KEY
  if (!apiKey || !deploymentKey) return
  const experiment = Experiment.initializeWithAmplitudeAnalytics(deploymentKey)
  await experiment.start()
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean>
): void {
  amplitude.track(eventName, properties)
}

export function identifyUser(user: User, appName?: string): void {
  const identify = new amplitude.Identify()
  identify.set('role', user.role)
  identify.set('organisation_id', user.organisationId)
  if (appName) identify.set('app_name', appName)
  amplitude.identify(identify)
}

export function setUserProperty(
  key: string,
  value: string | number | boolean
): void {
  const identify = new amplitude.Identify()
  identify.set(key, value)
  amplitude.identify(identify)
}

export function resetAmplitudeUser(): void {
  amplitude.reset()
}
